// src/components/home/SpotlightCarousel.tsx
import React from 'react';
import { FaFire, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { SpotlightPost } from './types';
 
interface SpotlightCarouselProps {
  darkMode: boolean;
  posts: SpotlightPost[];
  currentIndex: number;
  spotlightRef: React.RefObject<HTMLDivElement>;
  onIndexChange: (index: number) => void;
}
 
export const SpotlightCarousel: React.FC<SpotlightCarouselProps> = ({
  darkMode, posts, currentIndex, spotlightRef, onIndexChange,
}) => {
  return (
    <div className="mb-6 relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FaFire style={{ color: '#e8a820', fontSize: '16px' }} />
          <h2
            className="font-black text-base"
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '3px',
              color: darkMode ? '#f0fafa' : '#0d1a1a',
            }}
          >
            SPOTLIGHT
          </h2>
        </div>
 
        {/* Dot indicators */}
        <div className="flex items-center gap-2">
          {posts.map((_, i) => (
            <button
              key={i}
              onClick={() => onIndexChange(i)}
              className="rounded-full transition-all"
              style={{
                height: '4px',
                width: i === currentIndex ? '20px' : '6px',
                background: i === currentIndex ? '#e8a820' : darkMode ? 'rgba(90,184,184,0.25)' : 'rgba(13,26,26,0.2)',
              }}
            />
          ))}
        </div>
      </div>
 
      {/* Carousel */}
      <div className="relative group">
        <div ref={spotlightRef} className="flex overflow-x-hidden snap-x snap-mandatory scroll-smooth">
          {posts.map(spotlight => (
            <div key={spotlight.id} className="w-full flex-shrink-0 snap-center">
              <div
                className="relative overflow-hidden cursor-pointer transition-all"
                style={{
                  borderRadius: '12px',
                  border: `1.5px solid ${darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)'}`,
                }}
              >
                <img src={spotlight.image} alt={spotlight.title} className="w-full h-56 object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(13,26,26,0.85) 0%, rgba(13,26,26,0.2) 50%, transparent 100%)' }} />
 
                {/* Badge */}
                <div className="absolute top-4 left-4">
                  <span
                    className="text-xs font-black px-3 py-1 rounded"
                    style={{ background: '#e8a820', color: '#0d1a1a', letterSpacing: '1px', border: '1.5px solid #0d1a1a', boxShadow: '2px 2px 0 #0d1a1a' }}
                  >
                    {spotlight.badge.toUpperCase()}
                  </span>
                </div>
 
                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-black text-lg mb-2" style={{ color: '#f0fafa', letterSpacing: '0.5px' }}>
                    {spotlight.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src={`https://i.pravatar.cc/40?u=${spotlight.author}`} alt={spotlight.author}
                        className="w-5 h-5 rounded-full" style={{ border: '1.5px solid rgba(240,250,250,0.5)' }} />
                      <span className="text-xs font-bold" style={{ color: 'rgba(240,250,250,0.85)' }}>{spotlight.author}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-bold" style={{ color: '#e8a820' }}>
                      <FaFire style={{ fontSize: '10px' }} />
                      <span>{spotlight.views}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
 
        {/* Prev/Next */}
        {[
          { dir: 'prev', side: 'left-3', idx: (currentIndex - 1 + posts.length) % posts.length, icon: <FaChevronLeft /> },
          { dir: 'next', side: 'right-3', idx: (currentIndex + 1) % posts.length, icon: <FaChevronRight /> },
        ].map(btn => (
          <button
            key={btn.dir}
            onClick={() => onIndexChange(btn.idx)}
            className={`absolute ${btn.side} top-1/2 -translate-y-1/2 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all`}
            style={{
              background: 'rgba(13,26,26,0.7)',
              border: '1.5px solid rgba(90,184,184,0.3)',
              color: '#7ecece',
              backdropFilter: 'blur(4px)',
            }}
          >
            {btn.icon}
          </button>
        ))}
      </div>
    </div>
  );
};